import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiHome,
  FiUsers,
  FiSettings,
  FiMenu,
  FiSend,
  FiClock,
  FiTag,
  FiLogOut,
  FiFileText,
  FiCalendar,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.png";

export default function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sidebarRef = useRef(null);
  const { logout } = useAuth();

  const menuItems = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: <FiHome size={20} />,
      exact: true,
    },
    {
      path: "/dashboard/contacts/list",
      label: "Contacts",
      icon: <FiUsers size={20} />,
      matchPaths: ["/dashboard/contacts/list", "/dashboard/contacts/add"],
    },
    {
      path: "/dashboard/contacts/categories",
      label: "Categories",
      icon: <FiTag size={20} />,
    },
    {
      path: "/dashboard/email/send",
      label: "Send Email",
      icon: <FiSend size={20} />,
    },
    {
      path: "/dashboard/email/history",
      label: "Email History",
      icon: <FiClock size={20} />,
    },
    {
      path: "/dashboard/templates/list",
      label: "Templates",
      icon: <FiFileText size={20} />,
      matchPrefix: "/dashboard/templates",
    },
    {
      path: "/dashboard/meetings",
      label: "Meetings",
      icon: <FiCalendar size={20} />,
      exact: true,
    },
    {
      path: "/dashboard/meetings/calendar",
      label: "Calendar",
      icon: <FiCalendar size={20} />,
    },
    {
      path: "/dashboard/settings",
      label: "Settings",
      icon: <FiSettings size={20} />,
    },
  ];

  // Check if current menu item is active
  const isItemActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    if (item.matchPaths) {
      return item.matchPaths.includes(location.pathname);
    }
    if (item.matchPrefix) {
      return location.pathname.startsWith(item.matchPrefix);
    }
    return location.pathname === item.path;
  };

  // Close sidebar on outside click (mobile only)
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setIsOpen]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
    logout();
  };

  return (
    <aside
      ref={sidebarRef}
      className={`fixed md:static top-0 left-0 h-screen bg-white border-r border-gray-200 p-3
        transition-all duration-300 z-50 flex flex-col justify-between
        ${isCollapsed && !isOpen ? "w-20" : "w-64"} 
        ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}
    >
      <div>
        {/* Header */}
        <div className={`flex items-center mb-6 ${isCollapsed ? "justify-center" : "justify-between px-2"}`}>
          {!isCollapsed ? (
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <img src={logo} alt="logo" className="w-9 h-9" /> Mail Sender
            </h2>
          ) : (
            <img src={logo} alt="logo" className="w-8 h-8 mb-2 hidden md:block" />
          )}

          {/* Hamburger button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hidden md:block transition-colors"
          >
            <FiMenu size={20} />
          </button>
        </div>

        {/* Menu */}
        <ul className="space-y-1.5">
          {menuItems.map((item, idx) => {
            const active = isItemActive(item);
            return (
              <li key={idx}>
                <Link
                  to={item.path}
                  title={isCollapsed ? item.label : ""}
                  className={`flex items-center ${
                    isCollapsed ? "justify-center px-0 py-3" : "px-3 py-2.5"
                  } rounded-lg font-medium transition ${
                    active
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <span className={active ? "text-white" : "text-gray-600"}>{item.icon}</span>
                  {!isCollapsed && <span className="ml-3">{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        title={isCollapsed ? "Logout" : ""}
        className={`flex items-center ${
          isCollapsed ? "justify-center py-3" : "px-3 py-2.5"
        } mb-2 rounded-lg font-medium text-red-600 hover:bg-red-50 transition`}
      >
        <FiLogOut size={20} />
        {!isCollapsed && <span className="ml-3">Logout</span>}
      </button>
    </aside>
  );
}
